import type { SessionTask, SessionTaskStatus } from '@localagent/sdk';
import { cn } from '@localagent/ui';

const STATUS_LABEL: Record<SessionTaskStatus, string> = {
  pending: 'pending',
  in_progress: 'in progress',
  blocked: 'blocked',
  done: 'done',
  abandoned: 'abandoned',
};

const STATUS_CLASS: Record<SessionTaskStatus, string> = {
  pending: 'bg-muted text-muted-foreground',
  in_progress: 'bg-info-subtle text-info-text',
  blocked: 'bg-warning-subtle text-warning-text',
  done: 'bg-success-subtle text-success-text',
  abandoned: 'bg-muted text-muted-foreground',
};

export interface TaskRowProps {
  task: SessionTask;
  bySeq: Map<number, SessionTask>;
  testIdPrefix?: string;
}

export function TaskRow({ task, bySeq, testIdPrefix = '' }: TaskRowProps) {
  const finished = task.status === 'done' || task.status === 'abandoned';
  return (
    <li
      data-testid={`${testIdPrefix}task-row`}
      data-task-id={task.id}
      data-status={task.status}
      className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1.5 border-b border-border px-3 py-2 last:border-b-0"
    >
      <span className="pt-0.5 font-mono text-micro text-muted-foreground">{task.id}</span>
      <span
        data-testid={`${testIdPrefix}task-title`}
        className={cn('min-w-0 break-words text-body', finished && 'text-muted-foreground', task.status === 'abandoned' && 'line-through')}
      >
        {task.title}
      </span>
      <span
        data-testid={`${testIdPrefix}task-status`}
        className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-micro', STATUS_CLASS[task.status])}
      >
        {task.status === 'in_progress' && (
          <span
            data-testid={`${testIdPrefix}task-spinner`}
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-full border-2 border-current border-r-transparent motion-safe:animate-spin"
          />
        )}
        {STATUS_LABEL[task.status]}
      </span>
      {task.depends_on.length > 0 && (
        <div className="col-start-2 col-end-4 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}task-deps`}>
          {task.depends_on.map((seq) => {
            const dep = bySeq.get(seq);
            const done = dep?.status === 'done';
            return (
              <span
                key={seq}
                data-testid={`${testIdPrefix}task-dep`}
                data-dep-id={`T${seq}`}
                data-done={done ? 'true' : 'false'}
                className={cn(
                  'rounded-sm border px-1.5 font-mono text-micro',
                  done ? 'border-success text-success-text' : 'border-border text-muted-foreground',
                )}
              >
                {done ? '✓ ' : ''}T{seq}
              </span>
            );
          })}
        </div>
      )}
      {task.status === 'blocked' && task.note && (
        <div
          data-testid={`${testIdPrefix}task-blocked-note`}
          className="col-start-2 col-end-4 rounded-md bg-warning-subtle px-2 py-1.5 text-caption text-warning-text"
        >
          {task.note}
        </div>
      )}
    </li>
  );
}
